// gpxFormat.ts
// =============
//
// Wave 14ca-fix42: GPX 1.1 serialize + parse.
// Wir nutzen das Format selber (kein lib-Dep) damit der Code transparent
// bleibt + minimal ist. GPX ist XML mit klarem Schema, fuer unsere Use-Cases
// reichen <trkpt lat lon> + optional <ele> + <time>.
//
// Use-Cases:
//   - Export My Rides (recording → .gpx download)
//   - Export My Custom Tours (route + cached elevation samples → .gpx)
//   - Import (User-uploaded .gpx → CustomTrail)
//
// Cross-Tool-Kompatibilitaet:
//   - Strava nimmt jedes valide GPX 1.0 / 1.1 mit <trkpt> an. <time> fuer
//     Activities relevant, fuer Routes egal.
//   - Garmin Connect / Wahoo / Komoot nehmen alle <trkpt> + optional <ele>.
//   - GPX 1.1 ist Standard, wir setzen den 1.1-Header.

import type { CustomTrail } from './customTrails';
import type { Ride } from './rideRecorder';
import { computeStats } from './rideRecorder';
import { getDb } from './storage/db';
import { CUSTOM_TRAIL_ID_PREFIX } from './customTrailAsTrail';

const GPX_HEADER =
  '<?xml version="1.0" encoding="UTF-8"?>\n' +
  '<gpx version="1.1" creator="All Day Turbo" xmlns="http://www.topografix.com/GPX/1/1">';

/** XML-escapen — minimaler Set, ausreichend fuer Trail-Namen / Notes. */
function xmlEscape(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
}

/** Sicherer Filename: alphanumerisch + Bindestrich/Underscore, kollabiert
    sonstige Zeichen zu '-'. Damit Browser-Download nicht haengen bleibt. */
export function safeFilename(s: string): string {
  return (
    s
      .normalize('NFKD')
      .replace(/[^\w\s-]/g, '')
      .trim()
      .replace(/\s+/g, '-')
      .toLowerCase() || 'export'
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Serialize
// ─────────────────────────────────────────────────────────────────────────────

/** Konvertiert einen Recording-Ride (RidePoint[]) zu GPX. Mit timestamps,
    elevation wenn verfuegbar. Pro <trkpt> ein Punkt, eine <trkseg>. */
export function rideToGpx(ride: Ride): string {
  const stats = computeStats(ride.points);
  const startedAt = ride.startedAt;
  const km = (stats.distanceMeters / 1000).toFixed(2);
  const climb = Math.round(stats.elevationGainM);
  const name = `Ride ${new Date(startedAt).toLocaleDateString()} — ${km} km · ↑${climb} m`;

  const lines: string[] = [];
  lines.push(GPX_HEADER);
  lines.push('  <metadata>');
  lines.push(`    <name>${xmlEscape(name)}</name>`);
  lines.push(`    <time>${startedAt}</time>`);
  lines.push('  </metadata>');
  lines.push('  <trk>');
  lines.push(`    <name>${xmlEscape(name)}</name>`);
  lines.push('    <trkseg>');
  for (const p of ride.points) {
    const parts: string[] = [];
    parts.push(`      <trkpt lat="${p.lat}" lon="${p.lon}">`);
    if (typeof p.ele === 'number' && Number.isFinite(p.ele)) {
      parts.push(`        <ele>${p.ele.toFixed(1)}</ele>`);
    }
    if (typeof p.t === 'number' && Number.isFinite(p.t)) {
      parts.push(`        <time>${new Date(p.t).toISOString()}</time>`);
    }
    parts.push('      </trkpt>');
    lines.push(parts.join('\n'));
  }
  lines.push('    </trkseg>');
  lines.push('  </trk>');
  lines.push('</gpx>');
  return lines.join('\n');
}

/** Konvertiert eine Custom Tour zu GPX. Wenn cached elevation samples in
    IDB existieren (trailElevations store unter pseudo-id), interpoliert
    pro coord die Elevation und schreibt <ele> mit. Keine timestamps —
    Custom Tours sind Routen, nicht Recordings. */
export async function customTourToGpx(tour: CustomTrail): Promise<string> {
  const pseudoId = `${CUSTOM_TRAIL_ID_PREFIX}${tour.id}`;
  let elevationSamples: number[] | null = null;
  let smoothingSampleLengthM: number | null = null;
  try {
    const db = await getDb();
    const cached = await db.get('trailElevations', pseudoId);
    if (cached && Array.isArray(cached.samples) && cached.samples.length > 0) {
      elevationSamples = cached.samples;
      smoothingSampleLengthM = cached.trackLengthMeters;
    }
  } catch (err) {
    console.warn('[gpx] custom-tour elevation read failed', err);
  }

  const lines: string[] = [];
  lines.push(GPX_HEADER);
  lines.push('  <metadata>');
  lines.push(`    <name>${xmlEscape(tour.name)}</name>`);
  lines.push(`    <time>${tour.createdAt}</time>`);
  lines.push('  </metadata>');
  lines.push('  <trk>');
  lines.push(`    <name>${xmlEscape(tour.name)}</name>`);
  lines.push('    <trkseg>');

  // Cumulative-Distance pro coord — fuer Elevation-Interpolation gegen die
  // gleichmaessig verteilten samples (siehe sampleDistances in trailGeom).
  const cumDist: number[] = [0];
  for (let i = 1; i < tour.coords.length; i++) {
    const a = tour.coords[i - 1]!;
    const b = tour.coords[i]!;
    cumDist.push(cumDist[i - 1]! + haversineMeters(a[1], a[0], b[1], b[0]));
  }
  const totalDist = cumDist[cumDist.length - 1] ?? 0;

  for (let i = 0; i < tour.coords.length; i++) {
    const [lon, lat] = tour.coords[i]!;
    const parts: string[] = [];
    parts.push(`      <trkpt lat="${lat}" lon="${lon}">`);
    if (
      elevationSamples &&
      smoothingSampleLengthM &&
      totalDist > 0 &&
      elevationSamples.length > 1
    ) {
      // Linear interpolieren: elevation samples sind GLEICHMAESSIG ueber
      // smoothingSampleLengthM verteilt. Wir mappen unsere cumDist ins
      // sample-Intervall.
      const u = cumDist[i]! / totalDist;
      const fIdx = u * (elevationSamples.length - 1);
      const i0 = Math.floor(fIdx);
      const i1 = Math.min(i0 + 1, elevationSamples.length - 1);
      const t = fIdx - i0;
      const ele = elevationSamples[i0]! * (1 - t) + elevationSamples[i1]! * t;
      if (Number.isFinite(ele)) {
        parts.push(`        <ele>${ele.toFixed(1)}</ele>`);
      }
    }
    parts.push('      </trkpt>');
    lines.push(parts.join('\n'));
  }

  lines.push('    </trkseg>');
  lines.push('  </trk>');
  lines.push('</gpx>');
  return lines.join('\n');
}

// ─────────────────────────────────────────────────────────────────────────────
// Parse (Import)
// ─────────────────────────────────────────────────────────────────────────────

export interface ParsedGpx {
  name: string | null;
  /** Track-Punkte als [lon, lat]-Tupel — gleicher Format wie CustomTrail.coords. */
  coords: Array<[number, number]>;
  /** Strecken-Distanz in Metern (haversine sum). */
  lengthM: number;
  /** Elevation-Punkte falls in <ele> vorhanden. Pro Punkt ein Wert oder null. */
  elevations: Array<number | null>;
}

/** Parsed eine GPX-XML-Datei in lat/lon-Punkte und optionale Elevation.
    Toleriert verschiedene GPX-Varianten (1.0, 1.1; namespace-loose). */
export function parseImportedGpx(xml: string): ParsedGpx {
  if (typeof DOMParser === 'undefined') {
    throw new Error('DOMParser_unavailable');
  }
  const parser = new DOMParser();
  const doc = parser.parseFromString(xml, 'application/xml');

  // Parse-Errors detektieren — Browser legt ein <parsererror> rein.
  const errNode = doc.querySelector('parsererror');
  if (errNode) {
    throw new Error('invalid_xml: ' + (errNode.textContent ?? '').slice(0, 200));
  }

  // Name aus <metadata><name> oder <trk><name>.
  const name =
    doc.querySelector('metadata > name')?.textContent?.trim() ||
    doc.querySelector('trk > name')?.textContent?.trim() ||
    null;

  // Track-Punkte sammeln. Wir akzeptieren <trkpt> egal in welcher <trk>/<trkseg>.
  // Reihenfolge: DOM-Order = Track-Order.
  const trkpts = Array.from(doc.getElementsByTagName('trkpt'));
  // Fallback auf <rtept> (Routes statt Tracks) — z.B. Komoot-Export.
  const rtepts = trkpts.length === 0 ? Array.from(doc.getElementsByTagName('rtept')) : [];
  const points = trkpts.length > 0 ? trkpts : rtepts;

  const coords: Array<[number, number]> = [];
  const elevations: Array<number | null> = [];
  for (const pt of points) {
    const lat = Number(pt.getAttribute('lat'));
    const lon = Number(pt.getAttribute('lon'));
    if (!Number.isFinite(lat) || !Number.isFinite(lon)) continue;
    coords.push([lon, lat]);
    const eleEl = pt.getElementsByTagName('ele')[0];
    const ele = eleEl ? Number(eleEl.textContent) : NaN;
    elevations.push(Number.isFinite(ele) ? ele : null);
  }

  if (coords.length < 2) {
    throw new Error('too_few_points');
  }

  let lengthM = 0;
  for (let i = 1; i < coords.length; i++) {
    const a = coords[i - 1]!;
    const b = coords[i]!;
    lengthM += haversineMeters(a[1], a[0], b[1], b[0]);
  }

  return { name, coords, lengthM, elevations };
}

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

const EARTH_R = 6371000;
function toRad(d: number): number {
  return (d * Math.PI) / 180;
}
function haversineMeters(
  aLat: number,
  aLon: number,
  bLat: number,
  bLon: number,
): number {
  const phi1 = toRad(aLat);
  const phi2 = toRad(bLat);
  const dphi = toRad(bLat - aLat);
  const dlam = toRad(bLon - aLon);
  const h =
    Math.sin(dphi / 2) ** 2 +
    Math.cos(phi1) * Math.cos(phi2) * Math.sin(dlam / 2) ** 2;
  return 2 * EARTH_R * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));
}

// ─────────────────────────────────────────────────────────────────────────────
// Browser download helper
// ─────────────────────────────────────────────────────────────────────────────

/** Triggert einen Browser-Download fuer den gegebenen GPX-String.
    filename ohne ".gpx"-Suffix — wird automatisch angehaengt. */
export function downloadGpx(filename: string, gpx: string): void {
  const blob = new Blob([gpx], { type: 'application/gpx+xml' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${safeFilename(filename)}.gpx`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  // URL leak nach dem next tick freigeben.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
